/**
 * The authenticated user's MAIN navigation — single source of truth.
 *
 * The navbar renders this list twice (desktop `<ul>` + mobile menu) and
 * `router/AuthenticatedLayout.routes.ts` derives its slug↔route maps from it,
 * so a page added here becomes reachable everywhere at once. It exists because
 * the opposite happened: `/lots` and `/tax-loss-harvesting` shipped fully built
 * (routes, endpoints, es/en catalogs, E2E journeys) yet had no entry in either
 * hand-written menu, so they were reachable only by typing the URL.
 *
 * `labelKey` / `ariaLabelKey` are resolved by the navbar's
 * `useTranslation('shared')`; the `shared` namespace has no `navigation`
 * subtree, so i18next's `fallbackNS: 'common'` serves them from
 * `packages/resources/i18n/{en,es}/common.json`.
 */

export interface MainNavItem {
  /** Page slug passed to `onNavigate` (kept in Spanish for historical reasons). */
  readonly slug: string
  /** Real route; also the `href` so middle-click / "open in new tab" works. */
  readonly route: string
  /** `data-testid` of the desktop link (E2E selectors depend on these). */
  readonly testId: string
  /** `data-testid` of the mobile-menu link. */
  readonly mobileTestId: string
  /** i18n key of the visible label. */
  readonly labelKey: string
  /** i18n key of the `aria-label` (spells the full feature name). */
  readonly ariaLabelKey: string
}

export const AUTHENTICATED_MAIN_NAV_ITEMS: readonly MainNavItem[] = [
  {
    slug: 'transacciones',
    route: '/transactions',
    testId: 'nav-transactions',
    mobileTestId: 'navbar-mobile-link-transacciones',
    labelKey: 'navigation.transactions',
    ariaLabelKey: 'navigation.ariaLabels.goToTransactions',
  },
  {
    slug: 'importar',
    route: '/import',
    testId: 'nav-import',
    mobileTestId: 'navbar-mobile-link-importar',
    labelKey: 'navigation.import',
    ariaLabelKey: 'navigation.ariaLabels.goToImport',
  },
  {
    slug: 'saldos',
    route: '/balances',
    testId: 'nav-balances',
    mobileTestId: 'navbar-mobile-link-saldos',
    labelKey: 'navigation.balances',
    ariaLabelKey: 'navigation.ariaLabels.goToBalances',
  },
  {
    slug: 'matching',
    route: '/matching',
    testId: 'nav-matching',
    mobileTestId: 'navbar-mobile-link-matching',
    labelKey: 'navigation.matching',
    ariaLabelKey: 'navigation.ariaLabels.goToMatching',
  },
  {
    slug: 'informe-fiscal',
    route: '/tax-report',
    testId: 'nav-tax-report',
    mobileTestId: 'navbar-mobile-link-informe-fiscal',
    labelKey: 'navigation.taxReport',
    ariaLabelKey: 'navigation.ariaLabels.goToTaxReport',
  },
  // `/lots` and `/tax-loss-harvesting` are deliberately NOT listed.
  //
  // ⚠️ They are also no longer ROUTED: `Router.tsx` defines no `/lots` path and
  // no LotsPage component exists, so the URL now falls through the shell's
  // default to `/transactions` (verified in the browser 2026-08-09). This
  // comment used to say they stayed "reachable by URL"; that stopped being true
  // when the pages were removed, and the tax-report advice that pointed users
  // at "Seguimiento de lotes" was still sending them there.
]
